import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LogErrors } from './../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'

import { InstructorBase } from './../../../Layouts/Instructor/V2/InstructorBase'
import { Search } from './../../../Common/Tables/Search'
import { Loading } from './../../../Common/Loading'
import { HELP_TYPE } from './../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../utilities/EL/Roles'

// Excel utils
import { BuildAndDownloadWorkbook, JsonToSheet, FormatDateTime, BuildSheetWithMeta } from './../../../../utilities/BL/Instructor/ExcelExporter'

// Importaciones de la Capa de Negocios (Líder)
import { GetInstructorsList, GetCoursesForSelectedGroup, GetEvaluationsForSelectedCourse, GetAllResultsForInstructor, GetFilteredTableResults, GetDetailedSingleReport } from './../../../../utilities/BL/Leader/Results'


const Reports = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()
    const [isLoading, setIsLoading] = useState(true)

    // =========================================================================
    // 1. ESTADOS PARA FILTROS EN CASCADA (Instructor -> Grupo -> Curso -> Evaluación)
    // =========================================================================
    
    // --- Instructor ---
    const [instructorsList, setInstructorsList] = useState([])
    const [selectedInstructorId, setSelectedInstructorId] = useState(0)
    const [isInstructorsListLoading, setIsInstructorsListLoading] = useState(false)

    // --- Grupo --- (Se extraen localmente del Instructor seleccionado)
    const [groupsList, setGroupsList] = useState([])
    const [selectedGroupId, setSelectedGroupId] = useState(0)

    // --- Curso --- (Se solicitan a la API tras elegir el Grupo)
    const [coursesList, setCoursesList] = useState([])
    const [selectedCourseId, setSelectedCourseId] = useState(0)
    const [isCoursesListLoading, setIsCoursesListLoading] = useState(false)

    // --- Evaluación --- (Se solicitan a la API tras elegir el Curso)
    const [evaluationsList, setEvaluationsList] = useState([])
    const [selectedEvaluationId, setSelectedEvaluationId] = useState(0)
    const [isEvaluationsListLoading, setIsEvaluationsListLoading] = useState(false)


    // =========================================================================
    // 2. ESTADOS DE TABLA Y BÚSQUEDA
    // =========================================================================
    const [allRows, setAllRows] = useState([]) // Filas crudas de la API
    const [filteredRows, setFilteredRows] = useState([]) // Filas mostradas en tabla (afectadas por buscador)
    const [searchText, setSearchText] = useState('')
    const allRowsRef = useRef([])

    useEffect(() => { allRowsRef.current = allRows }, [allRows])

    // Filtro local en tiempo real para el buscador de texto
    useEffect(() => {
        const text = (searchText ?? '').toLowerCase()
        if (!text) { setFilteredRows(allRows); return }

        const filtered = allRows.filter(r => {
            const fields = [
                `${r?.name ?? ''} ${r?.lastName ?? ''}`,
                r?.user,
                r?.attemptNumber,
                r?.blockName ?? r?.nameEvaluation ?? r?.type,
                r?.finishDate ? FormatDateTime(r.finishDate) : '',
                r?.score
            ]
            return fields.some(v => String(v ?? '').toLowerCase().includes(text))
        })
        setFilteredRows(filtered)
    }, [searchText, allRows])


    // =========================================================================
    // 3. ESTADOS DE CONTROL Y PAGINACIÓN
    // =========================================================================
    const [isWorking, setIsWorking] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)

    const [pageNumber, setPageNumber] = useState(1)
    const [pageSize, setPageSize] = useState(50)
    const [hasMorePages, setHasMorePages] = useState(false)

    const [centerInfo, setCenterInfo] = useState({ idCenter: null, centerName: '' })


    // =========================================================================
    // 4. HELPERS DE APOYO (Exportación y Nombres)
    // =========================================================================
    const getEvaluationTypeName = (evaluationType, fallbackBlockName) => {
        switch (Number(evaluationType)) {
            case 1: return 'Diagnóstico'
            case 2: return fallbackBlockName || 'Parcial'
            case 3: return 'General'
            case 4: return 'Acreditación'
            case 5: return 'Certificación'
            default: return fallbackBlockName || 'Evaluación'
        }
    }

    const buildMetaRows = ({ header, metadata }) => {
        const { studentName, groupName, courseName } = header ?? {}
        const {
            evaluationType, finishDate, attemptNumber,
            totalQuestions, correctQuestions, incorrectQuestions, score, blockName
        } = metadata ?? {}

        return [
            ['Nombre:',                      studentName ?? '-'],
            ['Grupo:',                       groupName ?? '-'],
            ['Curso:',                       courseName ?? '-'],
            ['Evaluación:',                  getEvaluationTypeName(evaluationType, blockName)],
            ['Fecha en que se presentó:',    finishDate ? FormatDateTime(finishDate) : '-'],
            ['Número de oportunidad:',       attemptNumber ?? '-'],
            ['Total de preguntas:',          totalQuestions ?? '-'],
            ['Preguntas correctas:',         correctQuestions ?? '-'],
            ['Preguntas incorrectas:',       incorrectQuestions ?? '-'],
            ['Calificación:',                score ?? '-'],
            [],
            ['Detalle de evaluación:', '']
        ]
    }

    const buildDetailObjectRows = ({ evaluationType, details, finalAveragePercent }) => {
        const isPartial = Number(evaluationType) === 2
        const headerOrder  = ['blockName', 'itemName', 'result']
        const headerLabels = isPartial
            ? { blockName: 'Nombre del bloque', itemName: 'Tema', result: 'Aprobado/No aprobado' }
            : { blockName: 'Nombre del bloque', itemName: 'Tema', result: 'Resultado' }

        const safeDetails = Array.isArray(details) ? details : []
        const rows = safeDetails.map(d => ({
            blockName: d?.blockName ?? '-',
            itemName:  d?.itemName  ?? '-',
            result:    `${d?.percent ?? 0}%`
        }))

        rows.push({ blockName: '', itemName: '', result: '' })
        rows.push({ blockName: '', itemName: '', result: '' })
        rows.push({ blockName: 'Promedio', itemName: '', result: `${finalAveragePercent ?? 0}%` })

        return { headerOrder, headerLabels, rows }
    }

    const buildWorksheetForReport = ({ header, metadata, details, finalAveragePercent }) => {
        try {
            if (!metadata) throw new Error('metadata vacío')
            if (metadata?.evaluationType == null) throw new Error('evaluationType ausente en metadata')
            
            const metaRows = buildMetaRows({ header, metadata })
            const { headerOrder, headerLabels, rows } = buildDetailObjectRows({
                evaluationType: metadata.evaluationType,
                details: Array.isArray(details) ? details : [],
                finalAveragePercent
            })

            const colWidths = [32, 64, 16]
            return BuildSheetWithMeta({ metaRows, rows, headerOrder, headerLabels, colWidths })
        } catch (err) {
            console.error('buildWorksheetForReport:', err)
            const metaRows = [['Reporte', ''], ['Estado', `Error: ${err?.message ?? 'desconocido'}`]]
            return BuildSheetWithMeta({
                metaRows,
                rows: [{ note: 'Sin datos de detalle' }],
                headerOrder: ['note'],
                headerLabels: { note: 'Nota' },
                colWidths: [32]
            })
        }
    }


    // =========================================================================
    // 5. EFECTOS DE CARGA INICIAL
    // =========================================================================
    useEffect(() => {
        async function ValidateAndInit() {
            const tmpSession = await ValidateSession()
            if (tmpSession?.success) {
                // Validación para Líder (Ajustar if según tu UserStatus de líder)
                const user = tmpSession.data?.user
                const idCenter = user?.idCenter ?? user?.centerId ?? user?.center?.id ?? null
                const centerName = user?.centerName ?? user?.center?.name ?? ''
                setCenterInfo({ idCenter, centerName })
                
                await LoadInitialData()
            } else {
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        ValidateAndInit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const LoadInitialData = async () => {
        try {
            setIsWorking(true)
            setErrorMessage(null)
            setIsInstructorsListLoading(true)

            // Cargar listado inicial de instructores
            const response = await GetInstructorsList()
            if (response.success) {
                setInstructorsList(response.data)
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar los instructores.')
            }
        } catch (error) {
            await LogErrors('LoadInitialData - Results', error)
            setErrorMessage('Ocurrió un error al cargar la información inicial.')
        } finally {
            setIsWorking(false)
            setIsLoading(false)
            setIsInstructorsListLoading(false)
        }
    }


    // =========================================================================
    // 6. HANDLERS PARA CASCADA DE SELECTORES
    // =========================================================================
    
    /**
     * Paso 1: Seleccionar Instructor. Limpia todo hacia abajo y extrae los grupos localmente.
     */
    const handleSelectInstructor = (instructorId) => {
        setSelectedInstructorId(instructorId)
        
        // Reset cascada
        setSelectedGroupId(0)
        setSelectedCourseId(0)
        setSelectedEvaluationId(0)
        setGroupsList([])
        setCoursesList([])
        setEvaluationsList([])
        resetTableData()

        if (!instructorId) return

        // Extraer los grupos directamente del JSON del instructor para evitar otra llamada a API
        const selectedInstructor = instructorsList.find(inst => inst.idInstructor === instructorId)
        if (selectedInstructor && selectedInstructor.groups) {
            setGroupsList(selectedInstructor.groups)
        }
    }

    /**
     * Paso 2: Seleccionar Grupo. Solicita los Cursos pertenecientes a ese grupo e instructor.
     */
    const handleSelectGroup = async (groupId) => {
        try {
            setSelectedGroupId(groupId)
            
            // Reset cascada
            setSelectedCourseId(0)
            setSelectedEvaluationId(0)
            setCoursesList([])
            setEvaluationsList([])
            resetTableData()

            if (!groupId || !selectedInstructorId) return

            setIsCoursesListLoading(true)
            setIsWorking(true)
            setErrorMessage(null)

            const response = await GetCoursesForSelectedGroup(selectedInstructorId, groupId)
            if (response.success) {
                setCoursesList(response.data || [])
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar los cursos.')
            }
        } catch (e) {
            await LogErrors('handleSelectGroup - Results', e)
            setErrorMessage('Error interno al cargar los cursos.')
        } finally {
            setIsWorking(false)
            setIsCoursesListLoading(false)
        }
    }

    /**
     * Paso 3: Seleccionar Curso. Solicita las Evaluaciones disponibles.
     */
    const handleSelectCourse = async (courseId) => {
        try {
            setSelectedCourseId(courseId)
            
            // Reset cascada
            setSelectedEvaluationId(0)
            setEvaluationsList([])
            resetTableData()

            if (!courseId) return

            setIsEvaluationsListLoading(true)
            setIsWorking(true)
            setErrorMessage(null)

            const response = await GetEvaluationsForSelectedCourse(courseId)
            if (response.success) {
                setEvaluationsList(response.data || [])
            } else {
                setErrorMessage(response?.message ?? 'No fue posible cargar las evaluaciones.')
            }
        } catch (e) {
            await LogErrors('handleSelectCourse - Results', e)
            setErrorMessage('Error interno al cargar las evaluaciones.')
        } finally {
            setIsWorking(false)
            setIsEvaluationsListLoading(false)
        }
    }

    /**
     * Paso 4: Seleccionar Evaluación. Prepara el terreno para el botón Filtrar.
     */
    const handleSelectEvaluation = (evaluationId) => {
        setSelectedEvaluationId(evaluationId)
        resetTableData()
    }

    const resetTableData = () => {
        setAllRows([])
        setFilteredRows([])
        setSearchText('')
        setPageNumber(1)
        setHasMorePages(false)
    }


    // =========================================================================
    // 7. FUNCIONES PARA POBLAR TABLA Y PAGINACIÓN
    // =========================================================================
    
    const FetchResultsFromApi = async (page = pageNumber, size = pageSize) => {
        try {
            setIsLoading(true)

            if (!selectedInstructorId || !selectedGroupId || !selectedCourseId || !selectedEvaluationId) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Completa todos los filtros para continuar.' })
                return
            }

            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo resultados solicitados...' })
            setIsWorking(true)
            setErrorMessage(null)

            const requestData = {
                idInstructor: selectedInstructorId,
                idGroup: selectedGroupId,
                idCourse: selectedCourseId,
                idBlock: selectedEvaluationId,
                pageNumber: page,
                pageSize: size
            }

            const response = await GetFilteredTableResults(requestData)

            if (response.success) {
                const payload = response.data ?? {}
                const rowsReturned = Array.isArray(payload) ? payload : (payload.rows ?? [])
                
                setAllRows(rowsReturned)
                setFilteredRows(rowsReturned)

                if (!Array.isArray(payload)) {
                    setPageNumber(payload.page ?? page)
                    setPageSize(payload.pageSize ?? size)
                    setHasMorePages(Boolean(payload.hasMore))
                }
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mostrando resultados' })
            } else {
                resetTableData()
                showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message ?? 'No se encontraron resultados.' })
            }
        } catch (error) {
            await LogErrors('FetchResultsFromApi - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error interno al obtener los resultados de la tabla.' })
        } finally {
            setIsWorking(false)
            setIsLoading(false)
        }
    }

    const gotoPrevPage = () => {
        const prev = Math.max(1, pageNumber - 1)
        if (prev !== pageNumber) FetchResultsFromApi(prev, pageSize)
    }
    const gotoNextPage = () => {
        if (hasMorePages) FetchResultsFromApi(pageNumber + 1, pageSize)
    }


    // =========================================================================
    // 8. FUNCIONES DE EXPORTACIÓN A EXCEL
    // =========================================================================

    /**
     * Exportar TODOS los registros sin aplicar filtros (Botón Global)
     */
    const handleExportGlobalResults = async () => {
        try {
            if (!selectedInstructorId) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona un instructor primero.' })
                return
            }

            setIsLoading(true)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte completo. Esto puede demorar...' })
            
            const response = await GetAllResultsForInstructor(selectedInstructorId)

            if (response.success && response.data?.rows) {
                const rows = response.data.rows.map(e => ({
                    groupName: e.groupName,
                    courseName: e.courseName,
                    name: e.name,
                    lastName: e.lastName,
                    email: e.email,
                    user: e.user,
                    blockName: e.blockName,
                    attemptNumber: e.attemptNumber,
                    finishDate: FormatDateTime(e.finishDate),
                    score: e.score
                }))

                const headerOrder = ['groupName', 'courseName', 'name', 'lastName', 'email', 'user', 'blockName', 'attemptNumber', 'finishDate', 'score']
                const headerLabels = {
                    groupName: 'Nombre del grupo',
                    courseName: 'Curso',
                    name: 'Nombre(s)',
                    lastName: 'Apellidos',
                    email: 'Correo',
                    user: 'Usuario',
                    blockName: 'Tipo de evaluación',
                    attemptNumber: 'Oportunidad',
                    finishDate: 'Fecha',
                    score: 'Calificación'
                }
                const colWidths = [18, 16, 16, 16, 28, 16, 48, 12, 18, 14]
                
                const ws = JsonToSheet(rows, headerOrder, headerLabels, colWidths)
                const instNameFile = (response.data.instructorName || 'Instructor').replace(/[\\/:*?"<>|]/g,'_')
                
                BuildAndDownloadWorkbook(`Reporte_Completo_${instNameFile}_${new Date().toISOString().slice(0,10)}.xlsx`, [
                    { name: 'Resultados', ws }
                ])

                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay información disponible para exportar.' })
            }
        } catch (error) {
            await LogErrors('handleExportGlobalResults - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error fatal al exportar el documento.' })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Exportar la tabla actualmente visible y filtrada
     */
    const handleExportFilteredTable = async () => {
        try {
            setIsLoading(true)

            if (!filteredRows?.length) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay datos para exportar en la tabla.' })
                return
            }

            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Exportando tabla...' })

            const instructorName = (instructorsList ?? []).find(i => i.idInstructor === selectedInstructorId)?.instructorName ?? '—'
            const groupName      = (groupsList ?? []).find(g => g.idGroup === selectedGroupId)?.groupName ?? '—'
            const courseName     = (coursesList ?? []).find(c => c.idCourse === selectedCourseId)?.nameCourse ?? '—'
            const evaluationName = (evaluationsList ?? []).find(e => e.idBlock === selectedEvaluationId)?.name ?? '—'

            const rows = filteredRows.map(r => ({
                fullName: `${r.name ?? ''} ${r.lastName ?? ''}`.trim(),
                user: r.user,
                attemptNumber: r.attemptNumber,
                blockName: r.blockName ?? r.nameEvaluation ?? r.type ?? '-',
                finishDate: r.finishDate ? FormatDateTime(r.finishDate) : '-',
                score: r.score
            }))

            const headerOrder = ['fullName', 'user', 'attemptNumber', 'blockName', 'finishDate', 'score']
            const headerLabels = {
                fullName: 'Nombre',
                user: 'Usuario',
                attemptNumber: 'Oportunidad',
                blockName: 'Tipo de evaluación',
                finishDate: 'Fecha y hora de finalización',
                score: 'Calificación'
            }
            const colWidths = [24, 16, 12, 38, 22, 14]

            const metaRows = [
                ['Instructor:',   instructorName],
                ['Grupo:',        groupName],
                ['Curso:',        courseName],
                ['Evaluación:',   evaluationName],
                ['Página actual:', pageNumber],
                ['Tamaño de página:', pageSize],
                ['Total registros (página):', rows.length]
            ]

            const ws = BuildSheetWithMeta({ metaRows, rows, headerOrder, headerLabels, colWidths })

            BuildAndDownloadWorkbook(`Resultados_Desglosados_${new Date().toISOString().slice(0,10)}.xlsx`, [
                { name: 'Resultados', ws }
            ])

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
        } catch (error) {
            await LogErrors('handleExportFilteredTable - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible exportar la tabla.' })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Exportar reporte super detallado de 1 solo registro
     */
    const handleExportSingleDetailReport = async (row) => {
        try {
            setIsLoading(true)
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo detalle profundo del estudiante...' })

            const groupNameFromState = (groupsList ?? []).find(g => g.idGroup === (row.idGroup ?? selectedGroupId))?.groupName ?? row.groupName ?? ''
            const courseNameFromState = (coursesList ?? []).find(c => c.idCourse === (row.idCourse ?? selectedCourseId))?.nameCourse ?? row.courseName ?? ''

            const payloadDetails = {
                studentId: row.idZtudiante ?? row.studentId ?? row.id ?? null,
                courseId: row.idCourse ?? selectedCourseId ?? null,
                evaluationToken: row.token,
                groupId: row.idGroup ?? selectedGroupId ?? null,
                groupName: groupNameFromState,
                attemptNumber: row.attemptNumber ?? 1
            }

            const response = await GetDetailedSingleReport(payloadDetails)
            
            if (!response?.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message ?? 'No fue posible obtener el detalle.' })
                return
            }

            const data = response.data ?? {}
            const { header, metadata, details, finalAveragePercent } = data

            const enrichedHeader = {
                ...header,
                groupName: header?.groupName || groupNameFromState,
                courseName: header?.courseName || courseNameFromState
            }

            const ws = buildWorksheetForReport({
                header: enrichedHeader,
                metadata,
                details,
                finalAveragePercent
            })

            const studentForFile = (enrichedHeader?.studentName || 'Alumno').replace(/[\\/:*?"<>|]/g,'_')
            const when = metadata?.finishDate ? FormatDateTime(metadata.finishDate).replace(/[\\/:*?"<>|]/g,'_') : new Date().toISOString().slice(0,10)
            const evalName = getEvaluationTypeName(metadata?.evaluationType, metadata?.blockName).replace(/[\\/:*?"<>|]/g,'_')

            BuildAndDownloadWorkbook(`Reporte_Detalle_${studentForFile}_${evalName}_${when}.xlsx`, [
                { name: 'Resultados', ws }
            ])

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte generado.' })
        } catch (error) {
            await LogErrors('handleExportSingleDetailReport - Results', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al generar el reporte individual.' })
        } finally {
            setIsLoading(false)
        }
    }


    // =========================================================================
    // 9. RENDER DE LA VISTA
    // =========================================================================
    
    return isLoading ? (
        <Loading />
    ) : (
        <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.LEADER.REPORTS} page={WEBPAGES.LEADER.REPORTS} role={ROLES.LEADER}>
            <section className='container Instructor__Reports'>
                <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Resultados de los Ztudiantes</p>

                {/* 1. SELECCIÓN DE INSTRUCTOR PARA HABILITAR EL REPORTE GENERAL */}
                <div className='Instructor__Results__Reports__Filters Instructor__SpaceUp' style={{ justifyContent: 'center' }}>
                    {isInstructorsListLoading ? (
                        <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                            <option>Cargando instructores...</option>
                        </select>
                    ) : (
                        <select
                            className='Instructor__Results__Reports__Filters__List'
                            value={selectedInstructorId}
                            onChange={(e) => handleSelectInstructor(parseInt(e.target.value, 10))}
                            disabled={isWorking || instructorsList.length === 0}
                        >
                            <option value={0}>1. Selecciona un instructor</option>
                            {instructorsList.map(inst => (
                                <option key={inst.idInstructor} value={inst.idInstructor}>{inst.instructorName}</option>
                            ))}
                        </select>
                    )}
                    
                    <button 
                        type='button' 
                        className='Btn__Ztudia--Primary m-auto m-md-0' 
                        onClick={handleExportGlobalResults}
                        disabled={selectedInstructorId === 0 || isWorking}
                    >
                        <i className='bi bi-filetype-xlsx'></i> Exportar todo
                    </button>
                </div>

                <p className='Instructor__Text--Center Instructor__SpaceUp'>----- O -----</p>

                {/* 2. CASCADA DE FILTROS: GRUPO -> CURSO -> EVALUACIÓN */}
                <div className='Instructor__Results__Reports__Filters'>
                    
                    {/* GRUPO */}
                    <select
                        className='Instructor__Results__Reports__Filters__List'
                        value={selectedGroupId}
                        onChange={(e) => handleSelectGroup(parseInt(e.target.value, 10))}
                        disabled={isWorking || selectedInstructorId === 0 || groupsList.length === 0}
                    >
                        <option value={0}>2. Selecciona un grupo</option>
                        {groupsList.map(g => (
                            <option key={g.idGroup} value={g.idGroup}>{g.groupName}</option>
                        ))}
                    </select>

                    {/* CURSO */}
                    {isCoursesListLoading ? (
                        <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                            <option>Cargando cursos...</option>
                        </select>
                    ) : (
                        <select
                            className='Instructor__Results__Reports__Filters__List'
                            value={selectedCourseId}
                            onChange={(e) => handleSelectCourse(parseInt(e.target.value, 10))}
                            disabled={isWorking || selectedGroupId === 0 || coursesList.length === 0}
                        >
                            <option value={0}>3. Selecciona un curso</option>
                            {coursesList.map(c => (
                                <option key={c.idCourse} value={c.idCourse}>{c.nameCourse}{c.level && ` (Nivel ${c.level})`}</option>
                            ))}
                        </select>
                    )}

                    {/* EVALUACIÓN */}
                    {isEvaluationsListLoading ? (
                        <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                            <option>Cargando evaluaciones...</option>
                        </select>
                    ) : (
                        <select
                            className='Instructor__Results__Reports__Filters__List'
                            value={selectedEvaluationId}
                            onChange={(e) => handleSelectEvaluation(parseInt(e.target.value, 10))}
                            disabled={isWorking || selectedCourseId === 0 || evaluationsList.length === 0}
                        >
                            <option value={0}>4. Selecciona evaluación</option>
                            {evaluationsList.map(ev => (
                                <option key={ev.idBlock} value={ev.idBlock}>{ev.name}</option>
                            ))}
                        </select>
                    )}

                    {/* APLICAR FILTRO A LA TABLA */}
                    <button
                        type='button'
                        className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__Filters__Button'
                        onClick={() => FetchResultsFromApi(1, pageSize)}
                        disabled={isWorking || !selectedInstructorId || !selectedGroupId || !selectedCourseId || !selectedEvaluationId}
                    >
                        <i className='bi bi-funnel'></i> Aplicar filtro
                    </button>
                </div>

                {/* 3. CONTROLES DE LA TABLA Y EXPORTACIÓN ACTUAL */}
                <div className='Instructor__SpaceUp'>
                    <Search onChange={setSearchText} extraClassName={'Instructor__Results--V2__Search'} />
                </div>

                <button
                    type='button'
                    className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__ExportButton'
                    onClick={handleExportFilteredTable}
                    disabled={!filteredRows?.length}
                >
                    <i className='bi bi-filetype-xlsx'></i> Exportar
                </button>

                {/* 4. LA TABLA */}
                <section className='Tables__Container Instructor__Results--V2__Reports__ContainerTable Tables--Shadow Tables--Background Instructor__SpaceUp'>
                    <table className='Tables__Table Instructor__Results--V2__Reports__ContainerTable__Table'>
                        <thead>
                            <tr className='Tables__Table__Row--Header'>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>Nombre</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>Usuario</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>Evaluación</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>Oportunidad</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>Finalización</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>Calificación</td>
                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'></td>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows?.length ? (
                                filteredRows.map((r, idx) => (
                                    <tr key={r.id ?? idx} className='Tables__Table__Row'>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>
                                            {`${r.name ?? ''} ${r.lastName ?? ''}`.trim()}
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>{r.user}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>
                                            {r.blockName ?? r.nameEvaluation ?? r.type ?? '-'}
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>{r.attemptNumber}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>
                                            {r.finishDate ? `${FormatDateTime(r.finishDate)} hrs.` : '-'}
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>{r.score ?? '-'}</td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>
                                            <button
                                                type='button'
                                                className='Btn__Ztudia--PrimaryOutline'
                                                onClick={() => handleExportSingleDetailReport(r)}
                                            >
                                                <i className='bi bi-filetype-xlsx'></i> Exportar
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr className='Tables__Table__Row'>
                                    <td className='Tables__Table__Row__Cell text-center py-4' colSpan={7}>
                                        {isWorking ? 'Consultando resultados en el servidor...' : 'Sin resultados por mostrar. Aplica los filtros superiores.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>

                {/* 5. PAGINACIÓN */}
                <div className='Instructor__Reports__Pagination'>
                    <p className='Instructor__Reports__Pagination__Info text-center mt-3'>Total de registros en esta página: {filteredRows.length}</p>
                    <p className='Instructor__Reports__Pagination__Info text-center mt-3'>Página {pageNumber}</p>

                    <div className='d-flex justify-content-center'>
                        {pageNumber > 1 && (
                            <button
                                type='button'
                                className='Btn__Ztudia--PrimaryOutline me-2'
                                disabled={pageNumber <= 1 || isWorking}
                                onClick={gotoPrevPage}
                            >
                                <i className="bi bi-caret-left-fill"></i> Anterior
                            </button>
                        )}

                        {hasMorePages && (
                            <button
                                type='button'
                                className='Btn__Ztudia--PrimaryOutline ms-2'
                                disabled={!hasMorePages || isWorking}
                                onClick={gotoNextPage}
                            >
                                Siguiente <i className="bi bi-caret-right-fill"></i>
                            </button>
                        )}
                    </div>
                </div>

                {errorMessage && errorMessage !== '' && (<p className='Instructor__Message--Error text-center mt-3'>{errorMessage}</p>)}
            </section>
        </InstructorBase>
    )
}

export { Reports };